import { X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router";
import logo from "../../Assest/logo.png";

export default function Navbar() {
  const [openMenu, setOpenMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleMenu = () => {
    setOpenMenu((prev) => !prev);
  };

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/#home", text: "Home", isRoute: false },
    { href: "/#service", text: "Services", isRoute: false },
    { href: "/#aboutus", text: "About", isRoute: false },
    { href: "/#contact", text: "Contact", isRoute: false },
    { href: "/careers", text: "Careers", isRoute: true },
  ];

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-20 transition-all duration-300 ${
        scrolled
          ? "bg-gradient-to-r from-blue-600/95 to-purple-600/95 backdrop-blur-md shadow-lg"
          : "bg-gradient-to-r from-blue-500/90 to-purple-500/90 backdrop-blur-sm"
      }`}
    >
      <div className="bl-container z-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/#home" className="flex-shrink-0 flex items-center">
            <img
              src={logo}
              alt="Company Logo"
              className="h-12 w-auto md:w-full transition-transform duration-300 hover:scale-105"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link, index) =>
              link.isRoute ? (
                <Link
                  key={index}
                  to={link.href}
                  className={`relative px-3 py-2 text-lg font-medium transition-all duration-300 hover:scale-105 ${
                    scrolled
                      ? "text-white hover:text-gray-400"
                      : "text-white hover:text-gray-400"
                  } group`}
                >
                  {link.text}
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gray-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                </Link>
              ) : (
                <a
                  key={index}
                  href={link.href}
                  className={`relative px-3 py-2 text-lg font-medium transition-all duration-300 hover:scale-105 ${
                    scrolled
                      ? "text-white hover:text-gray-400"
                      : "text-white hover:text-gray-400"
                  } group`}
                >
                  {link.text}
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gray-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                </a>
              )
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={handleMenu}
              className={`relative p-3 rounded-xl transition-all duration-300 transform hover:scale-110 ${
                scrolled
                  ? "text-white hover:bg-gray-100 shadow-md"
                  : "text-white hover:bg-white/10 bg-white/10"
              }`}
            >
              <div className="relative w-6 h-6">
                <span
                  className={`absolute top-0 left-0 w-6 h-0.5 bg-current transition-all duration-300 ${
                    openMenu ? "rotate-45 translate-y-2.5" : ""
                  }`}
                ></span>
                <span
                  className={`absolute top-2.5 left-0 w-6 h-0.5 bg-current transition-all duration-300 ${
                    openMenu ? "opacity-0" : ""
                  }`}
                ></span>
                <span
                  className={`absolute top-5 left-0 w-6 h-0.5 bg-current transition-all duration-300 ${
                    openMenu ? "-rotate-45 -translate-y-2.5" : ""
                  }`}
                ></span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {openMenu && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            onClick={handleMenu}
          ></div>

          {/* Mobile Menu Panel */}
          <div className="fixed right-0 top-0 h-screen w-80 max-w-[90vw] bg-white shadow-2xl transform translate-x-0 transition-transform duration-300">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-6 bg-gradient-to-r from-blue-500 to-purple-500 shadow-lg">
              <a
                href="/#home"
                onClick={handleMenu}
                className="flex items-center"
              >
                <img
                  src={logo}
                  alt="Company Logo"
                  className="h-10 w-auto filter brightness-0 invert"
                />
              </a>
              <button
                onClick={handleMenu}
                className="p-2 text-white hover:bg-white/20 rounded-full transition-all duration-200 hover:scale-110"
              >
                <X size={24} />
              </button>
            </div>

            {/* Mobile Menu Links */}
            <div className="py-6 px-4 bg-white">
              <nav className="space-y-3">
                {navLinks.map((link, index) =>
                  link.isRoute ? (
                    <Link
                      key={index}
                      to={link.href}
                      onClick={handleMenu}
                      className="group flex items-center px-4 py-4 text-lg font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 border border-transparent hover:border-blue-200 hover:shadow-md"
                    >
                      <span className="w-2 h-2 rounded-full bg-blue-500 mr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                      {link.text}
                    </Link>
                  ) : (
                    <a
                      key={index}
                      href={link.href}
                      onClick={handleMenu}
                      className="group flex items-center px-4 py-4 text-lg font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 border border-transparent hover:border-blue-200 hover:shadow-md"
                    >
                      <span className="w-2 h-2 rounded-full bg-blue-500 mr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                      {link.text}
                    </a>
                  )
                )}
              </nav>
            </div>

            {/* Mobile Menu Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gray-50 border-t border-gray-200">
              <div className="text-center">
                {/* <p className="text-sm text-gray-600 mb-3">Ready to get started?</p> */}
                <a href="/#contact">
                  <button
                    onClick={() => {
                      handleMenu();
                    }}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Contact Us
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
